import React, { useState } from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SendIcon, DownloadIcon, FileTextIcon, AlertCircleIcon } from 'lucide-react';
const PDF_URL = 'https://edvinhomehealthcaresolutions.com/wp-content/uploads/2016/12/employment-application.pdf';
export function ApplicationFormPage() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const [fileName, setFileName] = useState('');
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName('');
    }
  };
  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-light-alt focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400';
  const labelClass = 'block text-sm font-semibold text-dark mb-1.5';
  return <main data-id="element-397">
      <PageBanner title="Application Form" subtitle="Complete the form below to apply for a position with our team." breadcrumbs={[{
      label: 'Careers',
      path: '/employment'
    }, {
      label: 'Application Form',
      path: '/application-form'
    }]} data-id="element-398" />

      <section className="py-16 bg-light-alt" data-id="element-399">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-401">
            {/* Download Banner */}
            <div className="bg-light border border-primary/10 rounded-2xl p-6 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-402">
              <div className="flex items-start" data-id="element-403">
                <div className="bg-light-alt p-3 rounded-xl shadow-sm mr-4 flex-shrink-0 text-primary" data-id="element-404">
                  <DownloadIcon className="h-6 w-6" data-id="element-405" />
                </div>
                <div data-id="element-406">
                  <h3 className="font-bold text-dark mb-1" data-id="element-407">
                    Download Employment Application
                  </h3>
                  <p className="text-dark-muted text-sm" data-id="element-408">
                    Please download and fill the Employment Reference form and
                    attach the file in the form below.
                  </p>
                </div>
              </div>
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap bg-primary hover:bg-primary-dark text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-sm flex items-center text-sm" data-id="element-409">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-410" />
                Download Form
              </a>
            </div>

            {/* Application Form */}
            <div className="bg-light rounded-2xl p-8 md:p-10 border border-gray-200 shadow-card" data-id="element-411">
              <div className="flex items-center mb-8" data-id="element-412">
                <div className="bg-primary/10 p-3 rounded-xl mr-4" data-id="element-413">
                  <FileTextIcon className="h-7 w-7 text-primary" data-id="element-414" />
                </div>
                <div data-id="element-415">
                  <h2 className="text-2xl font-bold text-dark" data-id="element-416">
                    Application Form
                  </h2>
                  <p className="text-dark-muted text-sm mt-1" data-id="element-417">
                    All fields marked with * are required
                  </p>
                </div>
              </div>

              <form className="space-y-5" onSubmit={e => e.preventDefault()} data-id="element-418">
                {/* Row 1: Date & SSN */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-419">
                  <div data-id="element-420">
                    <label htmlFor="dateOfApplication" className={labelClass} data-id="element-421">
                      Date of Application{' '}
                      <span className="text-red-500" data-id="element-422">*</span>
                    </label>
                    <input type="date" id="dateOfApplication" required className={inputClass} data-id="element-423" />
                  </div>
                  <div data-id="element-424">
                    <label htmlFor="ssn" className={labelClass} data-id="element-425">
                      Social Security Number{' '}
                      <span className="text-red-500" data-id="element-426">*</span>
                    </label>
                    <input type="text" id="ssn" required className={inputClass} placeholder="XXX-XX-XXXX" data-id="element-427" />
                  </div>
                </div>

                {/* Full Name */}
                <div data-id="element-428">
                  <label htmlFor="fullName" className={labelClass} data-id="element-429">
                    Print Full Name <span className="text-red-500" data-id="element-430">*</span>
                  </label>
                  <input type="text" id="fullName" required className={inputClass} placeholder="Enter your full legal name" data-id="element-431" />
                </div>

                {/* Row 2: Home Phone & Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-432">
                  <div data-id="element-433">
                    <label htmlFor="homePhone" className={labelClass} data-id="element-434">
                      Home Phone
                    </label>
                    <input type="tel" id="homePhone" className={inputClass} placeholder="(XXX) XXX-XXXX" data-id="element-435" />
                  </div>
                  <div data-id="element-436">
                    <label htmlFor="mobile" className={labelClass} data-id="element-437">
                      Mobile <span className="text-red-500" data-id="element-438">*</span>
                    </label>
                    <input type="tel" id="mobile" required className={inputClass} placeholder="(XXX) XXX-XXXX" data-id="element-439" />
                  </div>
                </div>

                {/* Email */}
                <div data-id="element-440">
                  <label htmlFor="email" className={labelClass} data-id="element-441">
                    Email <span className="text-red-500" data-id="element-442">*</span>
                  </label>
                  <input type="email" id="email" required className={inputClass} placeholder="your.email@example.com" data-id="element-443" />
                </div>

                {/* Address */}
                <div data-id="element-444">
                  <label htmlFor="address" className={labelClass} data-id="element-445">
                    Address <span className="text-red-500" data-id="element-446">*</span>
                  </label>
                  <input type="text" id="address" required className={inputClass} placeholder="Street address" data-id="element-447" />
                </div>

                {/* Row 3: City, State, Zip */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-448">
                  <div data-id="element-449">
                    <label htmlFor="city" className={labelClass} data-id="element-450">
                      City <span className="text-red-500" data-id="element-451">*</span>
                    </label>
                    <input type="text" id="city" required className={inputClass} placeholder="City" data-id="element-452" />
                  </div>
                  <div data-id="element-453">
                    <label htmlFor="state" className={labelClass} data-id="element-454">
                      State <span className="text-red-500" data-id="element-455">*</span>
                    </label>
                    <input type="text" id="state" required className={inputClass} placeholder="State" data-id="element-456" />
                  </div>
                  <div data-id="element-457">
                    <label htmlFor="zip" className={labelClass} data-id="element-458">
                      Zip <span className="text-red-500" data-id="element-459">*</span>
                    </label>
                    <input type="text" id="zip" required className={inputClass} placeholder="Zip Code" data-id="element-460" />
                  </div>
                </div>

                {/* Position Applied For */}
                <div data-id="element-461">
                  <label htmlFor="position" className={labelClass} data-id="element-462">
                    Position Applied For <span className="text-red-500" data-id="element-463">*</span>
                  </label>
                  <select id="position" required className={inputClass} data-id="element-464">
                    <option value="" data-id="element-465">Select a position</option>
                    <option value="Certified Medication Assistant" data-id="element-466">
                      Certified Medication Assistant
                    </option>
                    <option value="Home Health Aide" data-id="element-467">Home Health Aide</option>
                    <option value="Licensed Practical Nurse (LPN)" data-id="element-468">
                      Licensed Practical Nurse (LPN)
                    </option>
                    <option value="Registered Nurse (RN)" data-id="element-469">
                      Registered Nurse (RN)
                    </option>
                    <option value="Certified Nursing Assistant / GNA" data-id="element-470">
                      Certified Nursing Assistant / GNA
                    </option>
                    <option value="Other" data-id="element-471">Other</option>
                  </select>
                </div>

                {/* File Upload */}
                <div data-id="element-472">
                  <label className={labelClass} data-id="element-473">
                    Attach Employment Application Form if Needed
                  </label>
                  <div className="relative" data-id="element-474">
                    <label htmlFor="fileUpload" className="flex items-center justify-center w-full px-4 py-4 rounded-xl border-2 border-dashed border-gray-300 bg-light-alt hover:border-primary hover:bg-primary/5 transition-all cursor-pointer" data-id="element-475">
                      <div className="text-center" data-id="element-476">
                        <FileTextIcon className="h-6 w-6 text-gray-400 mx-auto mb-2" data-id="element-477" />
                        {fileName ? <span className="text-primary font-semibold text-sm" data-id="element-478">
                            {fileName}
                          </span> : <span className="text-dark-muted text-sm" data-id="element-479">
                            <span className="text-primary font-semibold" data-id="element-480">
                              Click to upload
                            </span>{' '}
                            or drag and drop
                          </span>}
                        <p className="text-xs text-gray-400 mt-1" data-id="element-481">
                          PDF, DOC, DOCX (max 10MB)
                        </p>
                      </div>
                    </label>
                    <input type="file" id="fileUpload" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange} data-id="element-482" />
                  </div>
                </div>

                {/* Subject */}
                <div data-id="element-483">
                  <label htmlFor="subject" className={labelClass} data-id="element-484">
                    Subject
                  </label>
                  <input type="text" id="subject" className={inputClass} placeholder="Subject or additional notes" data-id="element-485" />
                </div>

                {/* Submit */}
                <button type="submit" className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-white font-bold py-3.5 px-10 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center group" data-id="element-486">
                  <SendIcon className="mr-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-487" />
                  Send Application
                </button>
              </form>

              {/* Download reminder */}
              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-488">
                <div className="flex items-start bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-489">
                  <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-490" />
                  <p className="text-amber-800 text-sm" data-id="element-491">
                    Please download and fill the Employment Reference form (
                    <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline" data-id="element-492">
                      Click Here to Download Employment Reference form
                    </a>
                    ) and attach the file in the form above.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-493" />
    </main>;
}